/**
 * LearningPathPage.jsx — "Road to the Podium" dossier-timeline.
 *
 * The delegate's learning journey rendered as a confidential briefing
 * folio: a classified-stripe header, sealed envelope for locked
 * stations, gold wax-seal for completed ones, and a left-edge gold
 * spine that tracks progress like a vertical archive timeline.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "../../components/DashboardLayout";
import LoadingSkeleton from "../../components/LoadingSkeleton";
import api from "../../lib/api";
import {
  Compass, Gavel, Lightbulb, FileText, Edit3, Award, Lock, Sparkles,
  Trophy, CheckCircle2, ArrowRight, Stamp,
} from "lucide-react";


const ICONS = { Compass, Gavel, Lightbulb, FileText, Edit3, Award };

const LINKS = {
  orientation:    "/dashboard/lms",
  rules:          "/dashboard/lms",
  scenarios:      "/dashboard/scenarios",
  sandbox:        "/dashboard/sandbox",
  position_paper: "/dashboard/papers",
  podium:         "/dashboard/lms",
};


export default function LearningPathPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/lms/learning-path").then((r) => setData(r.data));
  }, []);

  if (!data) {
    return (
      <DashboardLayout>
        <div className="max-w-5xl mx-auto p-6">
          <LoadingSkeleton variant="page" label="Opening your dossier…" />
        </div>
      </DashboardLayout>
    );
  }

  const milestones = data.milestones.map((m, i, arr) => {
    const previousDone = arr.slice(0, i).every((x) => x.done);
    return {
      ...m,
      state: m.done ? "done" : (previousDone ? "available" : "locked"),
    };
  });
  const doneCount = milestones.filter((m) => m.state === "done").length;
  const progressPct = Math.round((doneCount / Math.max(milestones.length, 1)) * 100);

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto" data-testid="learning-path-root">
        <DossierHeader
          xp={data.xp}
          badges={data.badges?.length || 0}
          done={data.courses_completed}
          total={data.courses_total}
          scen_done={data.scenarios_completed}
          scen_total={data.scenarios_total}
          progressPct={progressPct}
        />

        <section className="relative mt-10 pl-8 sm:pl-14" data-testid="path-stations">
          {/* Vertical archive spine — gold hairline running the height */}
          <div aria-hidden className="absolute left-3 sm:left-6 top-0 bottom-0 w-px bg-gradient-to-b from-brand-gold/60 via-brand-gold/30 to-brand-gold/15" />
          {/* Progress overlay on the spine */}
          <div
            aria-hidden
            className="absolute left-3 sm:left-6 top-0 w-px bg-brand-gold transition-all duration-700"
            style={{ height: `${progressPct}%` }}
          />

          <ol className="space-y-8">
            {milestones.map((m, i) => (
              <Station
                key={m.id}
                m={m}
                index={i}
                isLast={i === milestones.length - 1}
              />
            ))}
          </ol>
        </section>
      </div>
    </DashboardLayout>
  );
}


function DossierHeader({ xp, badges, done, total, scen_done, scen_total, progressPct }) {
  return (
    <header
      className="relative overflow-hidden rounded-sm border border-brand-gold/30 bg-brand-navy-dark text-brand-champagne shadow-editorial"
      data-testid="path-header"
    >
      {/* Foil-stripe top */}
      <div aria-hidden className="absolute top-0 inset-x-0 h-0.5 bg-gradient-to-r from-transparent via-brand-gold to-transparent" />
      <div aria-hidden className="bg-noise absolute inset-0 pointer-events-none" />
      <div aria-hidden className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-gold/12 blur-3xl" />

      {/* Classified stripe — fake file-number meta */}
      <div className="relative border-b border-brand-gold/15 px-6 py-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.28em] text-brand-champagne/55">
        <span>FILE · DLG-{String(xp).padStart(6, "0")}</span>
        <span className="inline-flex items-center gap-1.5 text-brand-gold">
          <Stamp size={11} /> Confidential
        </span>
        <span className="hidden sm:inline">Status · Active</span>
      </div>

      <div className="relative p-7 sm:p-9">
        <p className="eyebrow text-brand-gold flex items-center gap-2">
          <Trophy size={11} /> Road to the Podium
        </p>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl text-brand-champagne leading-tight">
          Your journey to a perfect floor.
        </h1>
        <p className="mt-4 max-w-2xl text-sm text-brand-champagne/65 leading-relaxed">
          Six stations from first orientation to your moment at the rostrum.
          Each station unlocks the next — and your dossier seals as you advance.
        </p>

        {/* Progress wash bar */}
        <div className="mt-7 flex items-center gap-4">
          <div className="flex-1 h-1 bg-white/10 rounded-sm overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-brand-gold-dark via-brand-gold to-brand-gold-light transition-all duration-700"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <p className="font-mono text-sm text-brand-gold">
            {progressPct}%
          </p>
        </div>

        <div className="mt-7 grid sm:grid-cols-3 gap-4">
          <DossierStat label="XP earned"  value={xp.toLocaleString()} icon={Sparkles} testid="stat-xp" />
          <DossierStat label="Badges"     value={badges}              icon={Trophy}   testid="stat-badges" />
          <DossierStat label="Courses"    value={`${done} / ${total}`} icon={CheckCircle2} testid="stat-courses" />
        </div>
        {scen_total > 0 && (
          <p className="mt-4 text-[11px] text-brand-champagne/55 font-mono uppercase tracking-[0.22em]" data-testid="stat-scenarios">
            Scenario drills · {scen_done} of {scen_total} cleared
          </p>
        )}
      </div>
    </header>
  );
}


function DossierStat({ label, value, icon: Icon, testid }) {
  return (
    <div className="rounded-sm bg-white/5 border border-brand-gold/15 px-4 py-3" data-testid={testid}>
      <p className="eyebrow text-brand-gold flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {label}
      </p>
      <p className="mt-2 font-display text-2xl text-brand-champagne">{value}</p>
    </div>
  );
}


function Station({ m, index, isLast }) {
  const Icon = ICONS[m.icon] || Compass;
  const isLocked = m.state === "locked";
  const isDone = m.state === "done";
  const isAvailable = m.state === "available";
  const stationNum = String(index + 1).padStart(2, "0");

  // Station card spec
  const baseCls = "group relative rounded-sm border bg-white p-5 sm:p-6 transition-all duration-300";
  const cls = isDone
    ? `${baseCls} border-emerald-300 shadow-[0_8px_28px_-12px_rgba(16,185,129,0.35)]`
    : isAvailable
    ? `${baseCls} border-brand-gold shadow-gold-soft hover:shadow-gold-strong hover:-translate-y-0.5`
    : `${baseCls} border-slate-200 opacity-60`;

  const dotCls = isDone
    ? "bg-emerald-500 text-white ring-emerald-200"
    : isAvailable
    ? "bg-brand-gold text-brand-navy-dark ring-brand-gold/30 animate-gold-pulse"
    : "bg-slate-300 text-slate-500 ring-slate-200";

  const content = (
    <div className={cls} data-testid={`station-${m.id}`}>
      {/* Status badge in corner */}
      <span
        className={`absolute top-4 right-4 px-2 py-0.5 rounded-sm text-[9px] uppercase tracking-[0.22em] font-bold ${
          isDone
            ? "bg-emerald-100 text-emerald-700"
            : isAvailable
            ? "bg-brand-gold/15 text-brand-gold-dark"
            : "bg-slate-100 text-slate-500"
        }`}
      >
        {isDone ? "Sealed" : isAvailable ? "Open now" : "Sealed shut"}
      </span>

      <div className="flex items-start gap-4">
        {/* Big circular badge with icon */}
        <div className={`h-12 w-12 rounded-sm flex items-center justify-center ring-4 ${dotCls}`}>
          {isLocked ? <Lock className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="eyebrow text-brand-gold-dark">
            Station {stationNum}
          </p>
          <h3 className="mt-1.5 font-display text-2xl text-brand-navy-dark leading-tight">
            {m.label}
          </h3>
          {isAvailable && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] font-bold text-brand-midblue group-hover:text-brand-gold transition-colors">
              Continue dossier <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
            </p>
          )}
          {isDone && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">
              <CheckCircle2 className="h-3 w-3" /> Cleared — revisit notes
            </p>
          )}
          {isLocked && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] font-bold text-slate-400">
              <Lock className="h-3 w-3" /> Clear the previous station first
            </p>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <li className="relative">
      {/* Timeline node dot on the spine */}
      <span
        aria-hidden
        className={`absolute -left-[1.95rem] sm:-left-[2.2rem] top-7 h-3 w-3 rounded-full ring-4 ring-[#f6f8fb] ${
          isDone
            ? "bg-emerald-500"
            : isAvailable
            ? "bg-brand-gold animate-gold-pulse"
            : "bg-slate-300"
        }`}
      />
      {isLocked ? (
        content
      ) : (
        <Link to={LINKS[m.id] || "/dashboard"} data-testid={`station-${m.id}-link`}>
          {content}
        </Link>
      )}
      {!isLast && <span className="sr-only">→</span>}
    </li>
  );
}
